import { useEffect, useMemo, useState } from "react";
import { Stack, Snackbar, Alert } from "@mui/material";
import { GridRowSelectionModel } from "@mui/x-data-grid-premium";
import { getBankTransferTemplates, getPaymentDocuments } from "../api/payments";
import BankTransferTemplateSelect from "../components/payments/BankTransferTemplateSelect";
import PaymentsGrid from "../components/payments/PaymentsGrid";
import { generatePaymentFile } from "../api/payments";
import { BankTransferTemplateDTO } from "../types/BankTransferTemplateDTO";
import PaymentValidationDialog from "../components/payments/PaymentValidationDialog";
import { PaymentFilter } from "../types/payment";
import { buildGridStateStorageKey } from "../utils/gridStatePersistence";
import { apiFetch } from "../api/apiFetch";
import { OwnerOption } from "../components/documents/columns";

interface PaymentsPageProps {
  companyId: string;
}

export default function PaymentsPage({ companyId }: PaymentsPageProps) {
  const [templates, setTemplates] = useState<BankTransferTemplateDTO[]>([]);
  const [selectedTemplate, setSelectedTemplate] =
    useState<BankTransferTemplateDTO | null>(null);

  const [documents, setDocuments] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  const [statusFilter, setStatusFilter] = useState<PaymentFilter>("TO_PAY");

  const [selectionModel, setSelectionModel] =
    useState<GridRowSelectionModel>({
      type: "include",
      ids: new Set(),
    });

  const [invalidDocs, setInvalidDocs] = useState<any[]>([]);
  const [validationOpen, setValidationOpen] = useState(false);

  const selectedCount = selectionModel.ids.size;

  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [errorOpen, setErrorOpen] = useState(false);

  const gridStorageKey = useMemo(
    () =>
      buildGridStateStorageKey({
        table: "payments",
        companyId,
      }),
    [companyId]
  );

  const [ownerOptions, setOwnerOptions] = useState<OwnerOption[]>([]);

  useEffect(() => {
    const loadOwners = async () => {
      try {
        const resolveRes = await apiFetch(
          `${process.env.REACT_APP_API_URL}/companies/resolve?bcCompanyId=${companyId}`
        );
        const resolved = await resolveRes.json();

        const usersRes = await apiFetch(
          `${process.env.REACT_APP_API_URL}/companies/${resolved.id}/users`
        );
        const users = await usersRes.json();

        setOwnerOptions(
          users.map((u: any) => ({
            value: u.email,
            label: u.display_name || u.email,
          }))
        );
      } catch (err) {
        console.error("Błąd pobierania użytkowników firmy", err);
      }
    };

    loadOwners();
  }, [companyId]);

  const handleGenerateFile = async () => {
    if (!selectedTemplate || selectionModel.ids.size === 0) return;

    const selectedDocs = documents.filter((d) =>
      selectionModel.ids.has(d.systemId)
    );

    const invalidDocs = selectedDocs.filter(
      (d) => !d.canGeneratePaymentFile
    );

    if (invalidDocs.length > 0) {
      setInvalidDocs(invalidDocs);
      setValidationOpen(true);
      return;
    }

    const ids = Array.from(selectionModel.ids).join("|");

    try {
      const { blob, filename } = await generatePaymentFile(
        companyId,
        selectedTemplate.systemId,
        ids,
        selectedTemplate.transferFileExtension
      );

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      console.error("Payment generation error:", err);

      const message =
        err?.message ||
        err?.response?.data?.detail ||
        "Nie udało się wygenerować pliku płatności";

      setErrorMessage(message);
      setErrorOpen(true);
    }
  };

  const handleSelectionChange = (model: GridRowSelectionModel) => {
    if (model.type === "exclude") {
      const allIds = new Set(documents.map((d: any) => d.systemId));
      model.ids.forEach((id) => allIds.delete(id));
      setSelectionModel({ type: "include", ids: allIds });
    } else {
      setSelectionModel(model);
    }
  };

  useEffect(() => {
    if (!companyId) return;

    getBankTransferTemplates(companyId)
      .then(setTemplates)
      .catch((err) => {
        console.error("Failed to load templates", err);
        setTemplates([]);
      });
  }, [companyId]);

  useEffect(() => {
    if (!companyId || !selectedTemplate) {
      setDocuments([]);
      return;
    }

    let cancelled = false;

    setLoading(true);

    getPaymentDocuments(
      companyId,
      selectedTemplate.name,
      statusFilter
    )
      .then((data) => {
        if (cancelled) return;
        setDocuments(Array.isArray(data) ? data : []);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error("Failed to load payment documents", err);
        setDocuments([]);
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [companyId, selectedTemplate, statusFilter]);

  return (
    <Stack spacing={3} sx={{ flex: 1, minHeight: 0 }}>
      <BankTransferTemplateSelect
        templates={templates}
        value={selectedTemplate}
        onChange={(template) => {
          setSelectedTemplate(template);
          setStatusFilter("TO_PAY");
          setSelectionModel({
            type: "include",
            ids: new Set(),
          });
        }}
      />

      <Stack sx={{ flex: 1, minHeight: 0 }}>
        <PaymentsGrid
          gridStorageKey={gridStorageKey}
          rows={documents}
          loading={loading}
          selectionModel={selectionModel}
          onSelectionModelChange={handleSelectionChange}
          selectedCount={selectedCount}
          onGenerateFile={handleGenerateFile}
          statusFilter={statusFilter}
          onStatusFilterChange={setStatusFilter}
          ownerOptions={ownerOptions}
        />
      </Stack>

      <PaymentValidationDialog
        open={validationOpen}
        documents={invalidDocs}
        onClose={() => setValidationOpen(false)}
      />

      <Snackbar
        open={errorOpen}
        autoHideDuration={6000}
        onClose={() => setErrorOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          severity="error"
          variant="filled"
          onClose={() => setErrorOpen(false)}
          sx={{ width: "100%" }}
        >
          <strong>Błąd generowania płatności:</strong> {errorMessage}
        </Alert>
      </Snackbar>
    </Stack>
  );
}